import { useState, useEffect, useRef } from 'react';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { acquireApiToken } from '@/auth/acquireApiToken';
import { getCache, setCache } from '@/utils/cache';

export interface Event {
  id: number;
  title: string;
  date: string;
  time: string;
  type: string;
}

export const useCalendar = (): { events: Event[]; loading: boolean; error: string | null } => {
  const isAuthenticated = useIsAuthenticated();
  const { instance, accounts } = useMsal();
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const fetchedRef = useRef(false);
  const accountsRef = useRef(accounts);
  accountsRef.current = accounts;

  useEffect(() => {
    let isMounted = true;
    if (fetchedRef.current) {
      setLoading(false);
      return;
    }
    const cacheKey = 'calendarEvents_v1';
    const cached = getCache<Event[]>(cacheKey);
    if (cached) {
      if (isMounted) {
        setEvents(cached);
        setLoading(false);
        fetchedRef.current = true;
      }
      return;
    }
    const fetchCalendar = async () => {
      setLoading(true);
      setError(null);
      const apiUrl = process.env.REACT_APP_API_URL;
      if (!apiUrl) {
        if (isMounted) {
          setError('REACT_APP_API_URL not set');
          setLoading(false);
        }
        return;
      }
      const endpoint = `${apiUrl}/Home-Page/Calendar`;
      try {
        let token: string | null = null;
        const currentAccounts = accountsRef.current;
        if (isAuthenticated && currentAccounts.length > 0) {
          token = await acquireApiToken(instance, currentAccounts[0]);
        }
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (token) headers['Authorization'] = `Bearer ${token}`;
        const res = await fetch(endpoint, { method: 'GET', headers });
        const data = await res.json();
        if (!res.ok) {
          throw new Error(data.details || data.error || `HTTP ${res.status}`);
        }
        if (data.success && Array.isArray(data.events)) {
          const mapped: Event[] = data.events.map((ev: any) => {
            const start = ev.start?.dateTime || ev.created || null;
            let dateStr = start;
            let timeStr = '';
            if (start) {
              try {
                const dt = new Date(start);
                dateStr = dt.toISOString();
                timeStr = dt.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
              } catch {}
            }
            return {
              id: ev.id ?? Math.random(),
              title: ev.subject ?? ev.title ?? '(No title)',
              date: dateStr ?? '',
              time: timeStr || ev.time || '',
              type: (ev.categories && ev.categories[0]) || 'event',
            } as Event;
          });
          const now = new Date();
          const max = new Date();
          max.setDate(now.getDate() + 30);
          const filtered = mapped.filter((e) => {
            if (!e.date) return false;
            const d = new Date(e.date);
            return d >= now && d <= max;
          });
          if (isMounted) {
            setEvents(filtered);
            try { setCache('calendarEvents_v1', filtered, 300); } catch {}
            fetchedRef.current = true;
          }
        } else {
          if (isMounted) setEvents([]);
        }
      } catch (err: any) {
        if (isMounted) setError(err?.message ?? 'Failed to fetch calendar');
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchCalendar();
    return () => { isMounted = false; };
  }, [isAuthenticated, instance]);
  return { events, loading, error };
};
